/**
 * R-131-EPISTEMIC-GROUNDING-B — detect asserted absence without citation evidence.
 * R-131-EPISTEMIC-GROUNDING-D — contract-review scope gates Class 1 vs Class 2 negation.
 *
 * Two-pass: regex candidates, then Flash LLM judge for section-cited references.
 */
import { Type } from '@google/genai';
import { extractNumberedRefIds, maskNumberedRefTokens, NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import type {
  DataSource,
  ProjectIntent,
  ProjectIntentAttestations,
  SiteRagReport,
} from '../../types/siteRag';
import type { AuditSeverity } from '../../types/userAudit';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';
import { GLOBAL_ONLY_MODEL } from '../../config/geminiLocations';
import {
  GEMINI_CAPACITY_FALLBACK_MODEL,
  shouldUseCapacityFallback,
  toModelFallbackSummary,
  type ModelFallbackEntry,
  type ModelFallbackSummary,
} from '../../config/geminiCapacityFallback';
import { ai } from '../../services/geminiClient';
import {
  classifyContractReviewNegation,
  resolveContractReviewScopeForSection,
  shouldForceAbsenceFinding,
  shouldSuppressAbsenceFinding,
  type ContractReviewEpistemicScope,
  type ContractReviewNegationClass,
} from '../contractReviewEpistemic';
import { tracedGeminiCall } from '../telemetry/tracedGeminiCall';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import { extractSections, isSerializedRecord } from './reportTextExtract';
import { frozenFloodRefEvidence } from './absenceAssertionFloodEvidence';
import {
  ABSENCE_ASSERTION_JUDGE_PROMPT_VERSION,
  buildAbsenceAssertionJudgePrompt,
} from './prompts/absenceAssertionJudge';

export const ABSENCE_ASSERTION_JUDGE_MODEL = GEMINI_PRO_MODEL;
export const DEFAULT_MAX_CANDIDATES = 30;
export const JUDGE_TIMEOUT_MS = 30_000;
/**
 * Thinking budget for the judge on gemini-3.x models only. The judge is a
 * three-way classification, and 3.x default thinking pushed a probe past
 * the 30s race (31.5s, 2026-09-29). 2.5 models get no thinkingConfig, so
 * they behave as before.
 */
export const JUDGE_3X_THINKING_BUDGET = 1024;

export type AbsenceAssertionSuspectedClass = 'asserted-absence' | 'lack-of' | 'no-X-exists';
export type AbsenceAssertionRequiredEvidence = 'citation' | 'open-question';
export type AbsenceAssertionVerdict = 'supported' | 'unsupported' | 'ambiguous';

export interface AbsenceAssertionFinding {
  checkId: 'R-131-EPISTEMIC-GROUNDING-B';
  findingId: string;
  severity: 'block';
  sentenceText: string;
  suspectedClass: AbsenceAssertionSuspectedClass;
  /** R-131-EPISTEMIC-GROUNDING-D — clause vs document/fact negation when contract-review scope applies. */
  negationClass?: ContractReviewNegationClass;
  contractReviewScope?: ContractReviewEpistemicScope;
  requiredEvidence: AbsenceAssertionRequiredEvidence;
  sectionId: string;
  message: string;
  rationale?: string;
  supportingRefId?: number;
}

export interface AbsenceAssertionCandidate {
  id: string;
  sentenceText: string;
  sectionId: string;
  lineNumber: number;
  suspectedClass: AbsenceAssertionSuspectedClass;
  negationClass?: ContractReviewNegationClass;
  refIndices: number[];
}

export interface AbsenceAssertionJudgement {
  verdict: AbsenceAssertionVerdict;
  supportingRefId: number | null;
  rationale: string;
}

export interface AbsenceAssertionAuditResult {
  findings: AbsenceAssertionFinding[];
  /** Only the capped candidates actually processed by this audit. */
  candidateIdentities?: string[];
  candidatesScanned: number;
  candidatesJudged: number;
  suppressedCount: number;
  checkedAt: string;
  promptVersion: string;
  /** Truth-preserving contract-status rewrites applied before the audit. */
  autocorrectApplied?: boolean;
  rewritesApplied?: number;
  /** Judge calls a gemini-3.x judge served from 2.5. Present only when at least one happened. */
  modelFallbacks?: ModelFallbackSummary;
}

/** Stable sentence identity independent of citation spelling, labels and renumbering. */
export function absenceSentenceIdentity(sectionId: string, sentence: string): string {
  return `${sectionId}\u0000${sentence.normalize('NFKC')
    .replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), '')
    .replace(/<!--[^]*?-->/g, '')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\s+/g, ' ').trim().replace(/[.!?]+$/, '').toLocaleLowerCase('en-US')}`;
}

export interface AbsenceAssertionLlmContext {
  siteId?: string;
  reportId?: string;
  reportType?: string;
  /** Receives one entry per judgement that was served by the 2.5 fallback. */
  onModelFallback?: (entry: ModelFallbackEntry) => void;
}

export type AbsenceAssertionLlmFn = (
  prompt: string,
  ctx: AbsenceAssertionLlmContext,
) => Promise<string | null>;

const ABSENCE_PATTERNS: ReadonlyArray<{
  re: RegExp;
  suspectedClass: AbsenceAssertionSuspectedClass;
}> = [
  { re: /(?:has\s+not\s+been|is\s+not\s+yet)\s+\w+/i, suspectedClass: 'asserted-absence' },
  { re: /\b(?:the\s+)?lack\s+of\s+/i, suspectedClass: 'lack-of' },
  {
    /*
     * DETECTION GAP CLOSED (2026-08-05): the prior form allowed only ONE
     * optional Capitalized word between "no" and the verb, so a plain
     * multi-word subject escaped entirely — "No interconnection agreement
     * exists for this site" was never caught, letting a genuine unevidenced
     * absence ship. Up to three intervening words are now allowed; the
     * specific absence-verb anchors keep it tight, and the scope-boundary /
     * attributed-hedge exemptions above absorb the honest phrasings.
     */
    re: /\bno\s+(?:\w+\s+){0,3}(?:exists?|is\s+in\s+place|is\s+signed|(?:has|have)\s+been)\b/i,
    suspectedClass: 'no-X-exists',
  },
  {
    re: /\bnot\s+yet\s+(?:executed|signed|formed|established|binding|firm)/i,
    suspectedClass: 'asserted-absence',
  },
  {
    re: /\b(?:still\s+in\s+discussion|yet\s+to\s+be|remains\s+unresolved)\b/i,
    suspectedClass: 'asserted-absence',
  },
];

function hashCandidateId(sectionId: string, lineNumber: number, text: string): string {
  let h = 0;
  const key = `${sectionId}:${lineNumber}:${text.slice(0, 80)}`;
  for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  return `aa-${Math.abs(h).toString(36)}`;
}

function extractRefIndices(text: string): number[] {
  return extractNumberedRefIds(text).sort((a, b) => a - b);
}

function splitSentences(text: string, sectionId: string): Array<{
  text: string;
  sectionId: string;
  lineNumber: number;
}> {
  const lines = text.split('\n');
  const out: Array<{ text: string; sectionId: string; lineNumber: number }> = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trim();
    if (line.length < 12) continue;
    // Keep common inline examples within their governing sentence, so task
    // context is not detached at "ports of entry (e.g., Port of New Orleans)".
    const protectedLine = line.replace(/\b(?:e\.g\.|i\.e\.)/gi, token => token.replace(/\./g, '\uE000'));
    const re = /[^.!?\n]+(?:[.!?](?:\s|$)|$)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(protectedLine)) !== null) {
      const chunk = m[0].replace(/\uE000/g, '.').trim();
      if (chunk.length >= 12) {
        out.push({ text: chunk, sectionId, lineNumber: i + 1 });
      }
    }
    if (!/[.!?]/.test(line) && line.length >= 20) {
      out.push({ text: line, sectionId, lineNumber: i + 1 });
    }
  }
  return out;
}

function isOpenQuestion(text: string): boolean {
  const t = text.trim();
  if (t.endsWith('?')) return true;
  return /^(?:has|have|had|is|are|was|were|does|do|did|can|could|should|would|will)\b/i.test(t);
}

function isUnknownFraming(text: string): boolean {
  return /\b(?:does\s+not\s+address|not\s+addressed|documentation\s+(?:is\s+)?(?:silent|unclear)|unknown|unverified|unconfirmed|cannot\s+be\s+determined|not\s+documented|(?:Site\s+Evidence\s+Document|SED)\s+does\s+not\s+contain\s+verified\b[^.!?]*;\s*confirm\b[^.!?]*\bwhether)\b/i.test(
    maskNumberedRefTokens(text),
  );
}

/**
 * SCOPE-BOUNDARY framing (2026-08-05) — a sentence describing what an
 * estimate, timeline or study EXCLUDES is not claiming that evidence is
 * absent; it is stating a boundary, which is exactly the caveat an
 * investment-grade report should carry. The live 2026-08-04 Redhead report
 * was blocked for "this timeline does not include the physical construction
 * of major network upgrades" — a correct, valuable scope note.
 */
function isScopeBoundaryFraming(text: string): boolean {
  return /\b(?:does\s+not\s+include|do\s+not\s+include|excludes?|excluding|out\s+of\s+scope|beyond\s+the\s+scope|not\s+covered\s+by\s+this|assumes?\s+no)\b/i.test(
    text,
  );
}

/**
 * A contract-review clause pattern also matches ordinary dataset limitations:
 * "the snapshot does not include the time-series information ...". Preserve
 * that honest scope boundary without weakening real clause review. A sentence
 * naming a commercial/legal instrument or clause remains in the contract gate.
 */
function isNonContractScopeBoundaryFraming(text: string): boolean {
  if (!isScopeBoundaryFraming(text)) return false;
  const describesInformationArtifact =
    /\b(?:data|dataset|snapshot|analysis|estimate|heuristic|timeline|study|screen(?:ing)?|model|report)\b/i.test(text);
  const namesContractOrClause =
    /\b(?:agreement|contract|ppa|epc|msa|spa|sra|gssa|permit|approval|easement|clause|provision|article|legal\s+instrument)\b/i.test(text);
  return describesInformationArtifact && !namesContractOrClause;
}

/**
 * ATTRIBUTED HEDGE (2026-08-05, widened 2026-08-13) — an absence that is
 * explicitly sourced, hedged, or paired with a corroborating inference in
 * the SAME sentence is honest reporting, not an unevidenced claim. Live
 * 2026-08-13 (Redhead feasibility hold): "While firm transport is not yet
 * contracted, the proximity to a liquid trading hub (Gillis) suggests
 * capacity is available" — the absence is conceded and immediately
 * grounded by an inference marker. Requires BOTH the concessive
 * construction and an attribution/inference marker, so a bare "no
 * agreement exists" is still caught.
 */
function isAttributedHedge(text: string): boolean {
  const attributed =
    /\b(?:per\s+(?:the\s+)?developer|developer[- ]stated|developer[- ]provided|developer\s+documents|according\s+to|initial\s+discussions|preliminary\s+discussions|indicative|unverified|to\s+be\s+confirmed|pending\s+confirmation|suggests?|indicates?|is\s+expected|anticipated|likely)\b/i
      .test(text) || extractNumberedRefIds(text).length > 0;
  const hedged = /\b(?:while|although|though|however|but)\b/i.test(text);
  return attributed && hedged;
}

/**
 * REQUIREMENT FRAMING (2026-08-13) — a forward-looking requirements
 * statement ("This will require, at a minimum, a Title V Air Permit and a
 * firm gas transportation contract, which developers have not yet secured")
 * is a roadmap item, not an evidentiary claim about the world: the embedded
 * not-yet status is the very thing the requirement exists to close, and it
 * is exactly what an investment-grade feasibility report should say. Live
 * 2026-08-13: two Redhead feasibility holds were this shape.
 */
function isRequirementFraming(text: string): boolean {
  return /\b(?:will|would)\s+require\b|\brequires?,?\s+at\s+a\s+minimum\b|\brequired\s+(?:prior\s+to|before)\b|\bmust\s+(?:be\s+)?(?:secured?|obtain(?:ed)?|execut(?:e|ed)|complet(?:e|ed)|fil(?:e|ed))\b/i
    .test(text);
}

/**
 * EVIDENTIARY-SCOPE framing (2026-08-16) — a sentence stating that an
 * INVESTIGATORY ARTIFACT has not been produced is the report disclosing the
 * boundary of its own evidence base, not asserting an unevidenced fact about the
 * world. Live Redhead environmental hold: "An analysis of wildlife movement
 * patterns has not been conducted but would be a component of a full EIS" — a
 * correct, valuable scope caveat, and one an investment-grade report must make.
 *
 * Demanding a citation here is close to unfalsifiable: the evidence that a study
 * was never performed is the absence of the study. That asymmetry is why this
 * shape belongs with the other exemptions rather than in front of the judge.
 *
 * THE LINE THIS DRAWS, and it is deliberate: investigatory artifacts (analysis,
 * assessment, study, survey, delineation, inventory, stakeholder map) are
 * exempt; COMMERCIAL AND LEGAL INSTRUMENTS are NOT. "No interconnection
 * agreement exists", "the PPA is not yet executed", "no air permit has been
 * issued" are material claims about the project's real-world status and keep
 * blocking — that golden catch is pinned in the tests.
 */
function isEvidentiaryScopeFraming(text: string): boolean {
  /*
   * THE TWO FRAMES ARE NOT EQUALLY AMBIGUOUS, so they get different vocabularies.
   *
   * "<X> has not been conducted" forces the investigatory reading — you conduct a
   * study, never a scarcity — so a broad artifact list is safe there.
   *
   * "lack of <X>" does NOT: it takes artifacts and world-states equally well.
   * Caught by the real-body baseline pin (2026-08-16): a single shared list let
   * "This indicates a lack of baseline water scarcity, which provides a favorable
   * regulatory backdrop" be exempted on the word `baseline`. That sentence claims
   * something about the WORLD (water is not scarce) and must keep its evidence
   * burden. `baseline`, `inventory`, `mapping` and `modelling` are therefore
   * admitted only under the unambiguous frame.
   */
  const CONDUCTED_ARTIFACT = '(?:analys[ei]s|assessment|study|studies|survey|modell?ing|inventory|delineation|characteri[sz]ation|baseline|stakeholder\\s+map|mapping)';
  /** Nouns that can ONLY name an investigatory artifact, never a world-state. */
  const LACK_OF_ARTIFACT = '(?:analys[ei]s|assessment|study|studies|survey|delineation|characteri[sz]ation|stakeholder\\s+map)';

  // "<artifact> … has/have not been conducted/performed/…"
  const notConducted = new RegExp(
    `\\b${CONDUCTED_ARTIFACT}\\b[^.]{0,90}?\\b(?:has|have|had)\\s+not\\s+been\\s+(?:conducted|performed|completed|undertaken|prepared|commissioned|carried\\s+out|done)\\b`,
    'i',
  );
  // "lack of a (defined|formal|detailed) <artifact>"
  const lackOfArtifact = new RegExp(
    `\\b(?:lack|absence)\\s+of\\s+(?:a\\s+|an\\s+|any\\s+)?(?:defined\\s+|formal\\s+|detailed\\s+|comprehensive\\s+|site[- ]specific\\s+)?${LACK_OF_ARTIFACT}\\b`,
    'i',
  );
  // Explicit corpus-bounded disclosures ("not present/recorded in the reviewed
  // evidence", "no <X> is included in the uploaded documents") state what is
  // inside the reviewed package rather than asserting that <X> never exists or
  // was never performed in the real world.
  const notInReviewedEvidence =
    /\b(?:(?:is|are|was|were|remains?)\s+)?(?:not\s+(?:present|included|recorded|found|contained|documented|established)|unrecorded)\s+in\s+(?:the\s+)?(?:reviewed|provided|uploaded|available|supplied|developer(?:-provided|-supplied)?)\s+(?:evidence|documents?|documentation|records?|corpus|package|materials?)\b/i;
  const noItemInReviewedEvidence =
    /\bno\s+(?:[\w-]+\s+){0,6}(?:is|are)\s+(?:present|included|recorded|found|contained|documented)\s+in\s+(?:the\s+)?(?:reviewed|provided|uploaded|available|supplied)\s+(?:evidence|documents?|documentation|records?|corpus|package|materials?)\b/i;
  return (
    notConducted.test(text) ||
    lackOfArtifact.test(text) ||
    notInReviewedEvidence.test(text) ||
    noItemInReviewedEvidence.test(text)
  );
}

/** An uncomputed derived land result is an analysis limitation. Remove only
 * that status clause so a separate permit/contract/feature absence still checks. */
function withoutUncomputedLandStatus(text: string): string {
  return text.replace(/\b(?:(?:net\s+)?buildable|usable|developable)\s+(?:acreage|area|land(?:\s+area)?)\s+(?:(?:has|have)\s+not\s+(?:yet\s+)?been|is\s+not\s+yet)\s+(?:determined|calculated|computed|assessed|modeled|modelled|established)\b/gi, '');
}

/** A leading if-clause posits a contingency rather than asserting it.
 * Inspect the consequence separately: "If X, no agreement exists" still
 * asserts an absence and must retain its evidence requirement. */
function withoutConditionalAntecedent(text: string): string {
  return text.replace(/^\s*If\s+[^,;.!?]{1,220},\s*/i, '');
}

/** Undetermined applicability and unverified properties/assessments describe
 * unresolved evidence, not non-existent permits, studies or physical access. Split at
 * only those bounded predicates: a neighbouring absence must still be checked, without
 * borrowing an attribution/hedge from the uncertainty clause. Keep original
 * report prose intact for candidate IDs, citations and the evidence judge. */
function absenceAssertionFragments(text: string): string[] {
  const applicabilityStatus = /\b(?:(?:air[- ]permit|permit|permitting|PSD|Title\s+V)\s+applicability)(?:\s+(?:for|to)\s+(?:(?!\b(?:and|or|but|nor|while|although)\b)[^,;.!?]){1,100}?)?\s+(?:(?:has|have)\s+not\s+(?:yet\s+)?been|(?:is|are)\s+not\s+yet)\s+(?:determined|established|assessed|resolved)\b/gi;
  const unverifiedTransportAccess = /\b(?:lack|absence)\s+of\s+(?:verified|confirmed)\s+(?:(?:direct|on[- ]site)\s+)?(?:rail|port)(?:\s+(?:or|and)\s+(?:rail|port))?\s+access\b/gi;
  // Verification of an assessment/property is epistemic: "route feasibility
  // has not been verified" and "route feasibility is unverified" mean the
  // same thing. Require that positive subject and verification predicate;
  // never absorb a missing instrument/action (issued, signed, conducted), a
  // negated proposition, or another clause into the optional subject qualifier.
  // Determination nouns (2026-09-30, live Holly Ridge environmental ddp700 /
  // 6dr73l): the environmental contract asks for "applicability unresolved"
  // wording, and the report wrote "NEPA applicability remains unresolved
  // because …", "Land-use compatibility remains unresolved pending …", "A
  // complete compatibility analysis remains unresolved …", "A federal nexus is
  // not yet established from the available evidence." Each is the same
  // epistemic status as "applicability is undetermined" (already exempt): an
  // open determination, not a claim that a permit, study or agreement is
  // absent. The judge cannot ground a statement about the report's own open
  // question, so all of them held the report. Instruments and resources
  // (agreement, PPA, permit, gas supply, water rights) are deliberately NOT
  // listed: "The interconnection agreement remains unresolved" stays a
  // candidate for the evidence judge.
  const assessedProperty = '(?:feasibility|viability|suitability|adequacy|applicability|compatibility|capacity|availability|accessibility|potential|distance|latency|assessment|analysis|determination|nexus|pathway|estimate|findings?|results?|(?:(?:external|independent|utility|formal|third[- ]party)\\s+)*verification)';
  const qualifierWord = '(?:(?!\\b(?:but|nor|while|although|though|however|no|not|lack|absence|has|have|had|is|are|was|were|exists?|does|do)\\b|\\b(?:and|or)\\s+(?:(?:the|a|an|this)\\s+)?(?:permit|agreement|contract|PPA|entitlement|rights)\\b)[^,;:.!?])';
  // A noun example ("waterways, such as the Ouachita River,") belongs to
  // the assessed property. It cannot contain a separate assertion/absence.
  const propertyQualifier = `(?:\\s+(?:of|for|to)\\s+${qualifierWord}{1,180}?)?(?:,\\s+such\\s+as\\s+${qualifierWord}{1,100}?,)?`;
  const verificationStatus = '(?:(?:has|have)\\s+not\\s+(?:yet\\s+)?been\\s+(?:(?:independently|formally|fully)\\s+)?(?:verified|confirmed|determined|assessed|established)|(?:is|are|remains?)\\s+(?:not\\s+(?:yet\\s+)?(?:verified|confirmed|determined|assessed|established)|unverified|unconfirmed|undetermined|unassessed|unresolved))';
  const unverifiedAssessment = new RegExp(`\\b${assessedProperty}${propertyQualifier}\\s+${verificationStatus}\\b`, 'gi');
  const unverifiedMultiItemVerification = new RegExp(
    `\\b(?:(?:external|independent|utility|formal|third[- ]party)\\s+)*verification\\s+of\\s+(?:(?!\\b(?:but|nor|while|although|though|however|no|not|lack|absence|has|have|had|is|are|was|were|exists?|does|do)\\b)[^;:.!?]){1,240}?\\s+${verificationStatus}\\b`,
    'gi',
  );
  const pendingExternalVerification =
    /\b(?:grid\s+service|service\s+allocation|allocation)\s+remains\s+unresolved\s+pending\s+(?:external|independent|utility)\s+verification(?:\s+of\s+(?:(?!\b(?:but|nor|while|although|though|however|no|not|lack|absence)\b)[^,;.!?]){1,120})?\b/gi;
  const unconfirmedDeveloperRecord =
    /\b(?:based\s+on|recorded\s+in|stated\s+in)\s+(?:a\s+|an\s+|the\s+)?developer[- ](?:documented|provided|stated)\s+(?:agreement|record|documents?|basis)(?:,\s*which|\s+that)\s+(?:has|have)\s+not\s+(?:yet\s+)?been\s+(?:independently|externally|formally)\s+(?:confirmed|verified)(?:\s+with\s+(?:(?!\b(?:but|nor|while|although|though|however|no|not|lack|absence)\b)[^,;.!?]){1,140})?\b/gi;
  // A verification task does not establish its hoped-for negative result.
  // Strip only the bounded objective; an adjoining actual absence remains.
  // Present/past assertions ("confirms", "confirmed") are deliberately excluded.
  const absenceVerificationTask = /(?:^\s*(?:confirm|verify)|\b(?:and|by|to|must|should)\s+(?:confirm|verify|confirming|verifying))\s+(?:that|whether)\s+no\s+(?:\w+\s+){0,3}exists?\b/gi;
  const prospectiveTaskStart = /^\s*(?:[-*]\s*)?(?:(?:conduct|commission|perform|obtain|confirm|verify)\b|(?:this|the|a|an)\s+(?:study|assessment|survey|review)\s+(?:must|should|needs?\s+to|will\s+need\s+to)\b)/i;
  const splitVerificationTasks = (part: string): string[] => part.replace(absenceVerificationTask, (match, offset: number) => {
    const prefix = part.slice(0, offset).split(/;|,\s*(?:but|however|whereas)\b/i).at(-1) ?? '';
    // An earlier task must not lend its scope to a completed finding in a
    // subsequent clause, including "verified ... by confirming that ...".
    const completedFinding = /\b(?:verified|confirmed|completed|achieved|found|concluded|established|demonstrated|proved|reported|confirms|verifies|reports)\b/i.test(prefix);
    return prospectiveTaskStart.test(prefix + match) && !completedFinding ? '\uE001' : match;
  }).split('\uE001');
  const parts = text.split(applicabilityStatus)
    .flatMap(part => part.split(unverifiedTransportAccess))
    .flatMap(part => part.split(unverifiedAssessment))
    .flatMap(part => part.split(unverifiedMultiItemVerification))
    .flatMap(part => part.split(pendingExternalVerification))
    .flatMap(part => part.split(unconfirmedDeveloperRecord))
    .flatMap(splitVerificationTasks);
  if (parts.length === 1) return parts;
  return parts.flatMap(part => part.split(/\s*\b(?:but|while|although|though|however)\b\s*/i)).map(part => part
    .replace(/^\s*(?:[,;:]\s*)?(?:(?:and|but|while|although|though|however)\b\s*[,;:]?\s*)?/i, '')
    .replace(/\s*[,;:]?\s*\b(?:and|but|while|although|though|however)\s*$/i, '')
    .trim());
}

/** Test seam for the classifier's exemption rules (pure). */
export function classifySentenceForTest(text: string): AbsenceAssertionSuspectedClass | null {
  return classifySentence(text);
}

function classifySentence(text: string): AbsenceAssertionSuspectedClass | null {
  for (const fragment of absenceAssertionFragments(text)) {
    const classification = classifyAssertionFragment(fragment);
    if (classification) return classification;
  }
  return null;
}

function classifyAssertionFragment(text: string): AbsenceAssertionSuspectedClass | null {
  text = withoutUncomputedLandStatus(withoutConditionalAntecedent(text));
  // Exemptions run FIRST: these shapes are correct reporting, and this
  // detector is in the BLOCKING tier, so a false positive fails a true report.
  if (isScopeBoundaryFraming(text)) return null;
  if (isAttributedHedge(text)) return null;
  if (isRequirementFraming(text)) return null;
  if (isEvidentiaryScopeFraming(text)) return null;
  for (const { re, suspectedClass } of ABSENCE_PATTERNS) {
    if (re.test(text)) return suspectedClass;
  }
  return null;
}

/**
 * SCAN-GROUNDED SENTIMENT ABSENCE (2026-08-13) — in a public-sentiment
 * report, "lack of public discourse" is not an unevidenced claim when the
 * report's OWN scan metadata demonstrates it: sources were attempted and
 * returned zero observations. For a sentiment report, low discourse IS the
 * finding (the same never-hard-block philosophy already applied to the
 * dispatch gate) — holding the report for asserting what its own pipeline
 * measured contradicted the truth-only policy. Live 2026-08-13: three
 * Redhead PSR holds were this exact shape.
 */
const SENTIMENT_ABSENCE_TOPIC_RE =
  // 2026-08-14: second-order limitation sentences ("this lack of data
  // prevents a definitive assessment…") reference the scan's emptiness
  // without sentiment vocabulary in-sentence and were re-blocked at the
  // generation gate. Inside a PSR (this exemption is reportType-gated AND
  // scan-proven-gated), "lack/absence of data" refers to the sentiment scan.
  // 2026-08-16: `opposition` / `objection` were missing, so the single most
  // natural way to state the finding — live Redhead PSR, "The current lack of
  // public opposition provides a clean slate" — fell through and held the
  // report. It is the exemption's own premise said in plain English: the gate
  // below only opens when `opposedTechCount === 0`, i.e. the scan itself found
  // no opposition. The report was blocked for reporting its own data.
  /\b(?:public\s+(?:discourse|sentiment|opinion|comment|reaction|profile|opposition|oppositional\s+themes?|objection)|discourse|sentiment|opposition|oppositional\s+themes?|objection|media\s+coverage|community\s+(?:feedback|reaction|engagement)|observable\s+(?:trend|discussion)|no\s+trend|lack\s+of\s+(?:a\s+)?trend|(?:lack|absence)\s+of\s+(?:\w+\s+){0,2}data|no\s+(?:\w+\s+){0,2}data\b|inconclusive)\b/i;

export function isScanGroundedSentimentAbsence(
  report: SiteRagReport,
  sentenceText: string,
): boolean {
  if (report.reportType !== 'public-sentiment') return false;
  const meta = (report as {
    publicSentimentMeta?: {
      entryCount?: number;
      sourcesAttempted?: number;
      opposedTechCount?: number;
    };
  }).publicSentimentMeta;
  if ((meta?.sourcesAttempted ?? 0) <= 0) return false;
  // The scan proves absence-of-specific-discourse two ways: it found
  // NOTHING (entryCount 0), or everything it found is neutral noise
  // (opposedTechCount 0 — live Redhead: 12 uniform GDELT entries, zero
  // opposed technologies, classifier baseline). If the scan actually found
  // opposition, a "lack of discourse" claim contradicts the report's own
  // data and stays flagged.
  const scanProvedAbsence =
    (meta?.entryCount ?? Number.NaN) === 0
    || (meta?.opposedTechCount ?? Number.NaN) === 0;
  if (!scanProvedAbsence) return false;
  return SENTIMENT_ABSENCE_TOPIC_RE.test(sentenceText);
}

/**
 * A frozen SupplyChainPlan is evidence about the project's own procurement
 * ledger. When that immutable generation-time snapshot contains a non-empty
 * BoM and zero quote records, the report may state that the SNAPSHOT records
 * zero quotes / 0% recorded quote coverage without asking an LLM to prove a
 * negative from document prose.
 *
 * This is deliberately not a general commercial-absence exemption. Contract,
 * permit, reservation, award, signature and execution claims remain in the
 * ordinary fail-closed path, even when the same sentence also mentions quotes.
 */
export function isSupplyChainPlanGroundedQuoteAbsence(
  report: SiteRagReport,
  sentenceText: string,
): boolean {
  if (report.reportType !== 'supply-chain-risk') return false;
  const plan = report.supplyChainPlanAtGen;
  if (!plan || !Array.isArray(plan.quotes) || plan.quotes.length !== 0) return false;

  const quoteAbsence = /\b(?:(?:complete\s+)?(?:lack|absence)\s+of\s+(?:recorded\s+|formal\s+|firm\s+|written\s+)?quotes?|no\s+(?:recorded\s+|formal\s+|firm\s+|written\s+)?quotes?\s+(?:are\s+)?(?:recorded|available|attached|present|in\s+the\s+(?:ledger|snapshot))|(?:zero|0)\s+(?:recorded\s+|formal\s+|firm\s+|written\s+)?quotes?|0(?:\.0)?%\s+(?:recorded\s+)?quote\s+coverage)\b/i.test(sentenceText);
  if (!quoteAbsence) return false;

  // A percentage requires a denominator. Zero quote records alone ground
  // quote absence, but 0% coverage is meaningful only with at least one BoM
  // line in the frozen plan.
  if (/0(?:\.0)?%\s+(?:recorded\s+)?quote\s+coverage/i.test(sentenceText)) {
    if (!Array.isArray(plan.bom) || plan.bom.length === 0) return false;
  }

  // Keep material real-world status assertions fail-closed. "Firm quote" is
  // allowed; "firm contract/agreement/reservation" is not.
  const nonQuoteInstrument = /\b(?:ppa|epc|permit|approval|award|purchase\s+order|letter\s+of\s+intent|(?:slot\s+)?reservation|contract|agreement|commitment|(?:signed|executed|issued|approved|awarded|binding)(?!\s+quotes?\b))\b/i.test(sentenceText);
  return !nonQuoteInstrument;
}

function formatRefText(ref: DataSource | undefined, index: number): string {
  if (!ref) return `(Ref ${index} — metadata unavailable)`;
  const excerptParts = [
    ref.summary ? `summary=${ref.summary.trim()}` : null,
    ref.description ? `description=${ref.description.trim()}` : null,
    ref.notes ? `notes=${ref.notes.trim()}` : null,
    ref.excerpt ? `excerpt=${ref.excerpt.trim()}` : null,
    ref.excerptPreview ? `excerptPreview=${ref.excerptPreview.trim()}` : null,
    ref.sourceQuote ? `quote=${ref.sourceQuote.trim()}` : null,
    ref.rationale ? `rationale=${ref.rationale.trim()}` : null,
    Array.isArray(ref.evidenceQuotes) && ref.evidenceQuotes.length > 0
      ? `quotes=${ref.evidenceQuotes.map((q) => q.trim()).filter(Boolean).join(' | ')}`
      : null,
    Array.isArray(ref.subItems) && ref.subItems.length > 0
      ? `subItems=${ref.subItems.map((s) => `${s.topic}: ${s.detail}`.trim()).filter(Boolean).join(' | ')}`
      : null,
  ].filter(Boolean);
  return [
    ref.source,
    ref.field ? `field=${ref.field}` : null,
    ref.retrievedAt ? `retrieved=${ref.retrievedAt}` : null,
    ...excerptParts,
  ].filter(Boolean).join(' · ');
}

function selectRelevantDocumentExcerpts(
  textContent: string | undefined,
  candidateSentence: string | undefined,
): string[] {
  const raw = typeof textContent === 'string' ? textContent.trim() : '';
  if (!raw) return [];
  const chunks = raw
    .split(/\n+|\s{2,}|(?<=[.!?])\s+/)
    .map((c) => c.replace(/\s+/g, ' ').trim())
    .filter((c) => c.length >= 20);
  if (chunks.length === 0) return [raw.slice(0, 800)];

  const stopWords = new Set([
    'because', 'which', 'their', 'there', 'where', 'while', 'under', 'after',
    'before', 'between', 'through', 'about', 'against', 'within', 'without',
    'remains', 'remain', 'based', 'other', 'these', 'those', 'would', 'could',
    'should', 'have', 'been', 'from', 'with', 'that', 'this', 'site',
  ]);
  const queryTerms = (candidateSentence ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 4 && !stopWords.has(w));

  const scored = chunks.map((chunk, idx) => {
    const lower = chunk.toLowerCase();
    let score = 0;
    for (const term of queryTerms) {
      if (lower.includes(term)) score += 2;
    }
    if (/\b(?:no\s+environmental\s+professional|phase\s+i|delineation|unknown|unverified|unresolved|does\s+not\s+(?:establish|imply|issue|validate)|not\s+an\s+actual|separate\s+interfaces|confirmation\s+items?|facilities[- ]study)\b/i.test(chunk)) {
      score += 1;
    }
    return { chunk, idx, score };
  });

  scored.sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
  const picked: string[] = [];
  let totalChars = 0;
  for (const item of scored) {
    if (picked.length >= 4 || totalChars >= 1400) break;
    if (item.score <= 0 && picked.length > 0) break;
    const clipped = item.chunk.slice(0, 450);
    picked.push(clipped);
    totalChars += clipped.length;
  }
  return picked.length > 0 ? picked : [raw.slice(0, 600)];
}

function normalizeCitedDocumentId(raw: string | undefined | null): string {
  if (typeof raw !== 'string') return '';
  const withoutScheme = raw.trim().replace(/^doc:\/\//i, '').trim();
  const withoutFragmentOrQuery = withoutScheme.split(/[?#]/, 1)[0]?.trim() ?? '';
  return withoutFragmentOrQuery;
}

/**
 * Quote the matching frozen uploaded document summary and relevant excerpts
 * when the cited reference points to an uploaded document in the bound SED.
 *
 * Identity matching is strictly fail-closed (#240 #issuecomment-6596073860):
 *  - Citation fragments (`#page=4`, `?v=1#section=1.1`) are stripped before ID
 *    comparison, while fragment-only or conflicting `documentId` vs `doc://`
 *    locators fail closed.
 *  - A non-empty cited document ID matches ONLY by unambiguous ID (`docId`, or
 *    a single unambiguous `parentDocId` when no direct `docId` match exists) and
 *    NEVER falls back to filename matching when the ID does not match.
 *  - If both a cited ID and a cited `Uploaded document: <fileName>` are present
 *    and their filenames disagree, it fails closed.
 *  - Filename-only matching (when no document ID / `doc://` URL is present)
 *    requires a single unique filename match across `sed.uploadedDocuments`;
 *    duplicate filenames fail closed.
 */
function frozenUploadedDocumentRefEvidence(
  ref: DataSource | undefined,
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  candidateSentence?: string,
): string | undefined {
  if (
    !sed ||
    report.sedId !== sed.id ||
    report.sedVersionUsed !== sed.version ||
    typeof sed.version !== 'number' ||
    !Number.isInteger(sed.version) ||
    sed.version <= 0
  ) {
    return;
  }
  if (!ref || !Array.isArray(sed.uploadedDocuments) || sed.uploadedDocuments.length === 0) {
    return;
  }
  const rawDocumentId = typeof ref.documentId === 'string' ? ref.documentId.trim() : '';
  const rawUrl = typeof ref.url === 'string' ? ref.url.trim() : '';
  const hasDocUrl = /^doc:\/\//i.test(rawUrl);
  const explicitDocId = normalizeCitedDocumentId(rawDocumentId);
  const urlDocId = hasDocUrl ? normalizeCitedDocumentId(rawUrl) : '';

  // Fail closed on malformed fragment-only IDs or conflicting documentId vs doc:// URL.
  if (rawDocumentId.length > 0 && !explicitDocId) return;
  if (hasDocUrl && !urlDocId) return;
  if (explicitDocId && urlDocId && explicitDocId !== urlDocId) return;

  const refDocId = explicitDocId || urlDocId;
  const hasCitedId = rawDocumentId.length > 0 || hasDocUrl;
  const refFileName =
    typeof ref.source === 'string'
      ? ref.source
          .replace(/^\s*Uploaded\s+document:\s*/i, '')
          .split(/[?#]/, 1)[0]
          ?.trim() ?? ''
      : '';

  let matchedDoc: SiteEvidenceDocument['uploadedDocuments'][number] | undefined;
  if (hasCitedId) {
    const exactIdMatches = sed.uploadedDocuments.filter(
      (doc) => normalizeCitedDocumentId(doc.docId) === refDocId,
    );
    if (exactIdMatches.length > 1) return;
    if (exactIdMatches.length === 1) {
      matchedDoc = exactIdMatches[0];
    } else {
      const parentIdMatches = sed.uploadedDocuments.filter(
        (doc) => normalizeCitedDocumentId(doc.parentDocId) === refDocId,
      );
      if (parentIdMatches.length !== 1) return;
      matchedDoc = parentIdMatches[0];
    }
    // Fail closed if the reference also names a filename that conflicts with the matched document.
    if (
      refFileName &&
      typeof matchedDoc?.fileName === 'string' &&
      matchedDoc.fileName.trim() &&
      matchedDoc.fileName.trim().toLowerCase() !== refFileName.toLowerCase()
    ) {
      return;
    }
  } else {
    if (!refFileName) return;
    const nameMatches = sed.uploadedDocuments.filter(
      (doc) =>
        typeof doc.fileName === 'string' &&
        doc.fileName.trim().toLowerCase() === refFileName.toLowerCase(),
    );
    if (nameMatches.length !== 1) return;
    matchedDoc = nameMatches[0];
    const matchedDocNormId = normalizeCitedDocumentId(matchedDoc?.docId);
    if (
      matchedDocNormId &&
      sed.uploadedDocuments.filter((doc) => normalizeCitedDocumentId(doc.docId) === matchedDocNormId)
        .length > 1
    ) {
      return;
    }
  }
  if (!matchedDoc) return;

  const canonicalDocId = normalizeCitedDocumentId(matchedDoc.docId) || refDocId;
  const matchedFileNameNorm =
    typeof matchedDoc.fileName === 'string' ? matchedDoc.fileName.trim().toLowerCase() : '';
  const isUniqueFileNameInSed =
    matchedFileNameNorm.length > 0 &&
    sed.uploadedDocuments.filter(
      (doc) =>
        typeof doc.fileName === 'string' &&
        doc.fileName.trim().toLowerCase() === matchedFileNameNorm,
    ).length === 1;

  const summary =
    typeof matchedDoc.summary === 'string' && matchedDoc.summary.trim()
      ? matchedDoc.summary.trim().slice(0, 1200)
      : undefined;
  const excerpts = selectRelevantDocumentExcerpts(matchedDoc.textContent, candidateSentence);
  const docFacts = Array.isArray(sed.documentFacts)
    ? sed.documentFacts
        .filter((f) => {
          const factDocId = normalizeCitedDocumentId(f.documentId);
          const factFileNameNorm =
            typeof f.documentName === 'string' ? f.documentName.trim().toLowerCase() : '';
          if (canonicalDocId) {
            if (factDocId) {
              if (factDocId !== canonicalDocId) return false;
              if (factFileNameNorm && matchedFileNameNorm && factFileNameNorm !== matchedFileNameNorm) {
                return false;
              }
              return true;
            }
            return isUniqueFileNameInSed && factFileNameNorm === matchedFileNameNorm;
          }
          return !factDocId && isUniqueFileNameInSed && factFileNameNorm === matchedFileNameNorm;
        })
        .slice(0, 4)
        .map((f) => `${f.label}: ${f.value}${f.sourceQuote ? ` ("${f.sourceQuote.slice(0, 240)}")` : ''}`)
    : [];
  if (!summary && excerpts.length === 0 && docFacts.length === 0) return;

  return `\nFrozen uploaded document evidence (quoted evidence, not a verdict): ${JSON.stringify({
    sedId: sed.id,
    sedVersion: sed.version,
    documentId: normalizeCitedDocumentId(matchedDoc.docId) || (refDocId || undefined),
    fileName: matchedDoc.fileName,
    ...(summary ? { summary } : {}),
    ...(excerpts.length > 0 ? { excerpts } : {}),
    ...(docFacts.length > 0 ? { documentFacts: docFacts } : {}),
  })}`;
}

/** Quote only the current provider query belonging to this cited reference.
 * A facility fallback records zero even when the IX fetch failed, so that
 * payload can never establish a completed IX inventory or real-world absence.
 */
function frozenConnectivityRefEvidence(
  ref: DataSource | undefined,
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
): string | undefined {
  if (!sed || report.sedId !== sed.id || report.sedVersionUsed !== sed.version
    || typeof sed.version !== 'number' || !Number.isInteger(sed.version) || sed.version <= 0) return;
  const section = sed.connectivity;
  if (!ref || ref.field !== 'connectivity' || !section?.data
    || section.status !== 'verified' || section.attemptResult !== 'success'
    || section.attemptedProvider !== 'peeringdb-ix'
    || ref.qualification !== 'verified'
    || section.sourceUrl !== 'https://www.peeringdb.com/'
    || ref.source !== section.source || ref.url !== section.sourceUrl
    || ref.retrievedAt !== section.retrievedAt
    || !Number.isFinite(Date.parse(section.retrievedAt))) return;
  const point = section.queryPoint;
  if (!point || (point.source !== 'centroid' && point.source !== 'address')
    || !Number.isFinite(point.lat) || Math.abs(point.lat) > 90
    || !Number.isFinite(point.lng) || Math.abs(point.lng) > 180) return;
  const reportPoint = point.source === 'centroid'
    ? report.siteCentroid ?? report.location : report.location;
  if (!reportPoint || !sed.location
    || point.lat !== reportPoint.lat || point.lng !== reportPoint.lng
    || point.lat !== sed.location.lat || point.lng !== sed.location.lng) return;
  const data = section.data;
  if (['ixCount50km', 'facilityCount50km', 'nearestIxKind', 'nearestIxName', 'nearestIxDistKm']
    .some(field => section.docFieldSources?.[field] != null)) return;
  if (!Number.isInteger(data.ixCount50km) || data.ixCount50km < 0
    || !Number.isInteger(data.facilityCount50km) || data.facilityCount50km < 0
    || !Number.isFinite(data.nearestIxDistKm) || data.nearestIxDistKm < 0
    || typeof data.nearestIxName !== 'string' || !data.nearestIxName.trim()
    || data.nearestIxName.length > 200
    || (data.nearestIxKind !== 'ixp' && data.nearestIxKind !== 'colo-fallback')) return;
  // Contradictory IX count/distance tuples do not lend evidence to a claim.
  if (data.nearestIxKind === 'ixp'
    && ((data.nearestIxDistKm < 50 && data.ixCount50km === 0)
      || (data.nearestIxDistKm >= 50 && data.ixCount50km > 0))) return;
  return `\nFrozen connectivity provider query (quoted evidence, not a verdict): ${JSON.stringify({
    sedId: sed.id,
    sedVersion: sed.version,
    source: section.source,
    sourceUrl: section.sourceUrl,
    retrievedAt: section.retrievedAt,
    status: section.status,
    attemptResult: section.attemptResult,
    queryPoint: { lat: point.lat, lng: point.lng, source: point.source },
    radiusKm: 50,
    radiusComparison: 'distance < 50 km',
    ixCount50km: data.ixCount50km,
    facilityCount50km: data.facilityCount50km,
    nearestIxKind: data.nearestIxKind,
    nearestIxName: data.nearestIxName,
    nearestIxDistKm: data.nearestIxDistKm,
    ixInventoryCoverage: data.nearestIxKind === 'colo-fallback'
      ? 'unresolved: the facility fallback can retain zero after an IX-list failure; zero does not prove IX absence'
      : 'coordinate-bearing IX provider inventory only; not exhaustive real-world absence',
    scopeLimit: 'A colocation facility is not an IX. Inventory counts and geographic proximity do not establish routed fiber, carrier service or measured latency.',
  })}`;
}

function buildSectionRefs(
  sectionText: string,
  references: DataSource[],
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  candidateSentence?: string,
): Array<{ refId: number; text: string }> {
  const indices = extractRefIndices(sectionText);
  return indices.map((refId) => ({
    refId,
    text: formatRefText(references[refId - 1], refId)
      + (frozenConnectivityRefEvidence(references[refId - 1], report, sed) ?? '')
      + (frozenFloodRefEvidence(references[refId - 1], report, sed) ?? '')
      + (frozenUploadedDocumentRefEvidence(references[refId - 1], report, sed, candidateSentence) ?? ''),
  }));
}

/** Pass 1 — regex scan of narrative sections for absence-assertion candidates. */
export function extractAbsenceAssertionCandidates(report: SiteRagReport): AbsenceAssertionCandidate[] {
  const sections = extractSections(report);
  const md = (report as SiteRagReport & { markdown?: string }).markdown?.trim() ?? '';
  if (md.length > 100) {
    sections.unshift({ id: 'markdown', content: md });
  }

  const candidates: AbsenceAssertionCandidate[] = [];
  const seen = new Set<string>();

  for (const section of sections) {
    for (const sentence of splitSentences(section.content, section.id)) {
      const classified = absenceAssertionFragments(sentence.text).map(fragment => {
        if (isOpenQuestion(fragment) || isUnknownFraming(fragment)) return null;
        // An unresolved authority determination or an explicit contingency is
        // not a claim that a document/permit is absent. Keep subsequent
        // affirmative absence sentences under the ordinary detector.
        if (/^\s*Unresolved[,;:]\s+/i.test(fragment) && /\b(?:applicability|federal\s+nexus|agency\s+(?:determination|authority)|review\s+(?:pathway|trigger))\b/i.test(fragment)) return null;
        if (/^\s*Unresolved\s+(?:federal\s+nexus|(?:NEPA\s+)?review\s+pathway|permit\s+applicability)\s*[;,:]\s*(?:(?:NEPA\s+)?review\s+pathway|applicability)\s+(?:is|remains)\s+(?:unknown|undetermined|not\s+yet\s+determined)\.?\s*$/i.test(fragment)) return null;
        // A flattened object is machine serialization, not an assertion.
        if (isSerializedRecord(fragment) || /^doc:[^\s|]+\s+\|\s+Text:\s/i.test(fragment.trim())) return null;

        const suspectedClass = classifyAssertionFragment(fragment);
        const negationClass = classifyContractReviewNegation(withoutConditionalAntecedent(fragment));
        // Do not let generic contract-clause classification reintroduce an
        // ordinary dataset limitation; actual contract clauses stay blocked.
        if (!suspectedClass && negationClass === 'clause-absence' && isNonContractScopeBoundaryFraming(fragment)) return null;
        if (!suspectedClass && negationClass !== 'clause-absence') return null;
        return { suspectedClass, negationClass };
      }).find(result => result !== null);
      if (!classified) continue;
      const { suspectedClass, negationClass } = classified;
      const id = hashCandidateId(sentence.sectionId, sentence.lineNumber, sentence.text);
      if (seen.has(id)) continue;
      seen.add(id);

      candidates.push({
        id,
        sentenceText: sentence.text,
        sectionId: sentence.sectionId,
        lineNumber: sentence.lineNumber,
        suspectedClass: suspectedClass ?? 'asserted-absence',
        negationClass: negationClass ?? undefined,
        refIndices: extractRefIndices(sentence.text),
      });
    }
  }

  return candidates;
}

export function parseAbsenceAssertionJudgement(raw: string | null | undefined): AbsenceAssertionJudgement | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as {
      verdict?: string;
      supportingRefId?: unknown;
      rationale?: string;
    };
    const verdict = String(parsed.verdict ?? '').toLowerCase();
    if (verdict !== 'supported' && verdict !== 'unsupported' && verdict !== 'ambiguous') {
      return null;
    }
    const supportingRefId = parsed.supportingRefId == null
      ? null
      : Number(parsed.supportingRefId);
    return {
      verdict: verdict as AbsenceAssertionVerdict,
      supportingRefId: Number.isFinite(supportingRefId) && supportingRefId! > 0
        ? supportingRefId!
        : null,
      rationale: typeof parsed.rationale === 'string' ? parsed.rationale.trim() : '',
    };
  } catch {
    return null;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => { setTimeout(() => resolve(null), ms); }),
  ]);
}

function judgeRequest(model: string, prompt: string, ctx: AbsenceAssertionLlmContext) {
  return withTimeout(
    tracedGeminiCall(
      model,
      () => ai.models.generateContent({
        model,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              verdict: {
                type: Type.STRING,
                description: 'supported, unsupported, or ambiguous',
              },
              supportingRefId: { type: Type.NUMBER, nullable: true },
              rationale: { type: Type.STRING },
            },
            required: ['verdict', 'rationale'],
          },
          ...(GLOBAL_ONLY_MODEL.test(model)
            ? { thinkingConfig: { thinkingBudget: JUDGE_3X_THINKING_BUDGET } }
            : {}),
        },
      }),
      {
        siteId: ctx.siteId,
        reportId: ctx.reportId,
        reportType: ctx.reportType,
        stage: 'absence-assertion-judge',
      },
    ),
    JUDGE_TIMEOUT_MS,
  );
}

/**
 * Judge one candidate. A gemini-3.x judge that times out or hits a capacity
 * error (429 / RESOURCE_EXHAUSTED / 503) is retried ONCE on the GA 2.5 Pro.
 * Otherwise a missing judgement becomes a blocking "LLM judge unavailable"
 * finding and triggers a full truth-gate regeneration. 2.5 judges are never
 * retried, as before.
 */
export const defaultAbsenceAssertionLlmFn: AbsenceAssertionLlmFn = async (prompt, ctx) => {
  const primary = ABSENCE_ASSERTION_JUDGE_MODEL;
  let response: unknown = null;
  let failure: unknown = null;
  try {
    response = await judgeRequest(primary, prompt, ctx);
  } catch (err) {
    failure = err;
  }
  const retryOnFallback = GLOBAL_ONLY_MODEL.test(primary) && (
    failure
      ? shouldUseCapacityFallback(primary, (failure as Error)?.message ?? String(failure))
      : !response
  );
  const primaryFailure = failure;
  if (retryOnFallback) {
    console.warn(
      `[absenceAssertionDetector] ${primary} ${failure ? 'at capacity' : 'timed out'}; `
      + `retrying the judge once on ${GEMINI_CAPACITY_FALLBACK_MODEL}`,
    );
    failure = null;
    try {
      response = await judgeRequest(GEMINI_CAPACITY_FALLBACK_MODEL, prompt, ctx);
    } catch (err) {
      failure = err;
    }
  }
  if (failure) {
    console.warn('[absenceAssertionDetector] judge failed:', (failure as Error)?.message);
    return null;
  }
  if (!response) {
    console.warn('[absenceAssertionDetector] judge timed out or returned empty');
    return null;
  }
  if (retryOnFallback) {
    ctx.onModelFallback?.({
      callSite: 'absence-assertion-judge',
      requested: primary,
      served: GEMINI_CAPACITY_FALLBACK_MODEL,
      reason: primaryFailure ? 'capacity (429 / RESOURCE_EXHAUSTED / 503)' : 'timed out (30s race)',
    });
  }
  return (response as { text?: string }).text ?? null;
};

function findingFromCandidate(
  candidate: AbsenceAssertionCandidate,
  rationale: string,
  contractReviewScope?: ContractReviewEpistemicScope,
): AbsenceAssertionFinding {
  return {
    checkId: 'R-131-EPISTEMIC-GROUNDING-B',
    findingId: candidate.id,
    severity: 'block',
    sentenceText: candidate.sentenceText,
    suspectedClass: candidate.suspectedClass,
    ...(candidate.negationClass ? { negationClass: candidate.negationClass } : {}),
    ...(contractReviewScope ? { contractReviewScope } : {}),
    requiredEvidence: 'citation',
    sectionId: candidate.sectionId,
    message: `Asserted absence without evidence: "${candidate.sentenceText.slice(0, 120)}" — ${rationale}`,
    rationale,
  };
}

/**
 * One narrative assertion can be projected onto more than one reader-visible
 * report surface (for example `structuredFindings` and
 * `contextualKeyFindings`). The audit must still inspect every occurrence — a
 * cited copy must not hide an unsupported copy — but an identical unsupported
 * assertion is one truth defect and should produce one finding/hold reason.
 */
function absenceFindingIdentity(finding: AbsenceAssertionFinding): string {
  return finding.sentenceText
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

interface ContractStatusRewriteRule {
  reportTypes: readonly string[];
  key?: keyof ProjectIntentAttestations;
  label: string;
  question: string;
  patterns: RegExp[];
  resolve?: (
    report: SiteRagReport,
    intent: ProjectIntent | null | undefined,
  ) => string | undefined;
  renderResolved?: (state: string) => string;
  renderUnknown?: () => string;
}

const POWER_CONTRACT_STATUS_REWRITE_RULES: readonly ContractStatusRewriteRule[] = [
  {
    reportTypes: ['power-availability'],
    key: 'ppaSigned',
    label: 'PPA signed',
    question: 'Has a Power Purchase Agreement (PPA) been signed?',
    patterns: [
      /\b(?:a|the)\s+Power Purchase Agreement\s*\(PPA\)\s+has not been signed\.?/gi,
      /\bthe\s+PPA\s+has not been (?:signed|executed)\.?/gi,
    ],
  },
  {
    reportTypes: ['power-availability'],
    key: 'epcSigned',
    label: 'EPC contract signed',
    question: 'Has an EPC contract been signed?',
    patterns: [
      /\b(?:an|the)\s+EPC contract\s+has not been signed\.?/gi,
      /\bthe\s+EPC agreement\s+has not been (?:signed|executed)\.?/gi,
    ],
  },
  {
    reportTypes: ['power-availability'],
    key: 'gasSupplyFirm',
    label: 'firm-gas',
    question: 'What is the current status of the firm gas-supply agreement?',
    patterns: [
      /\b(?:the\s+)?firm gas supply agreement is currently in discussion but not yet executed\.?/gi,
      /\b(?:the\s+)?firm gas (?:supply|transport) agreement has not been executed\.?/gi,
    ],
  },
  {
    reportTypes: ['power-availability'],
    key: 'slotReservationBinding',
    label: 'binding equipment-slot reservation',
    question: 'Is a binding equipment-slot reservation in place?',
    patterns: [
      /\b(?:a|the)\s+binding (?:equipment-)?slot reservation (?:is not in place|has not been executed)\.?/gi,
    ],
  },
] as const;

const CROSS_REPORT_STATUS_REWRITE_RULES: readonly ContractStatusRewriteRule[] = [
  {
    reportTypes: ['public-sentiment'],
    key: 'gasSupplyFirm',
    label: 'firm-gas',
    question: 'What is the current status of the firm gas-supply agreement?',
    patterns: [
      /\ba separate long-term firm transportation contract,\s+which is not yet in place\.?/gi,
    ],
    renderResolved: (state) =>
      `a separate long-term firm transportation contract; the project's firm-gas status is operator-attested as "${state}".`,
    renderUnknown: () =>
      'a separate long-term firm transportation contract. Documentation is unclear on the project\'s firm-gas status. Open question: What is the current status of the firm gas-supply agreement?',
  },
  {
    reportTypes: ['environmental'],
    label: 'cultural-resources survey',
    question: 'Has a cultural-resources survey been completed?',
    patterns: [
      /\bNo cultural resources survey has been completed\.?/gi,
    ],
  },
  {
    reportTypes: ['environmental'],
    label: 'ISO/RTO for interconnection',
    question: 'Which ISO/RTO governs interconnection?',
    patterns: [
      /\bThe responsible ISO\/RTO for interconnection is not yet established\.?/gi,
    ],
    resolve: (report) => {
      const isoRegion = report.datasets?.grid?.isoRegion?.trim();
      if (!isoRegion || /^(?:unknown|n\/?a|not\s+(?:known|established))$/i.test(isoRegion)) {
        return undefined;
      }
      return isoRegion;
    },
    renderResolved: (isoRegion) => `Canonical ISO/RTO region: ${isoRegion}.`,
    renderUnknown: () =>
      'Documentation is unclear on the responsible ISO/RTO for interconnection. Open question: Which ISO/RTO governs interconnection?',
  },
] as const;

function resolveStatusRewriteRule(
  rule: ContractStatusRewriteRule,
  report: SiteRagReport,
  intent: ProjectIntent | null | undefined,
): string | undefined {
  const resolved = rule.resolve?.(report, intent);
  if (resolved?.trim()) return resolved.trim();
  if (!rule.key) return undefined;
  const state = intent?.attestations?.[rule.key];
  return state && state !== 'unknown' ? state : undefined;
}

function rewriteContractStatusText(
  text: string,
  report: SiteRagReport,
  intent: ProjectIntent | null | undefined,
  rules: readonly ContractStatusRewriteRule[],
): { text: string; applied: number; matchedRules: ContractStatusRewriteRule[] } {
  let out = text;
  let applied = 0;
  const matchedRules: ContractStatusRewriteRule[] = [];
  for (const rule of rules) {
    if (!rule.reportTypes.includes(report.reportType ?? '')) continue;
    const state = resolveStatusRewriteRule(rule, report, intent);
    const replacement = state
      ? rule.renderResolved?.(state) ?? `Operator-attested ${rule.label} status: ${state}.`
      : rule.renderUnknown?.() ?? `Documentation is unclear on ${rule.label} status. Open question: ${rule.question}`;
    for (const pattern of rule.patterns) {
      pattern.lastIndex = 0;
      if (!pattern.test(out)) continue;
      pattern.lastIndex = 0;
      out = out.replace(pattern, replacement);
      applied += 1;
      if (!matchedRules.includes(rule)) matchedRules.push(rule);
    }
  }
  return { text: out, applied, matchedRules };
}

function applyContractStatusDiscipline(
  report: SiteRagReport,
  intent: ProjectIntent | null | undefined,
  rules: readonly ContractStatusRewriteRule[],
): { report: SiteRagReport; applied: number } {
  let applied = 0;

  const walk = (value: unknown, path: string): unknown => {
    const pathSegments = path.split('.');
    if (
      pathSegments.includes('qaFindings')
      || pathSegments.includes('qualityHoldFindings')
      || pathSegments.includes('qualityReview')
      || pathSegments.includes('sanityFindings')
      || pathSegments.includes('references')
      || pathSegments.includes('evidence')
      // A target-site fact or attestation must never overwrite the documented
      // status of a third-party comparable project.
      || pathSegments.includes('comparableSiteBenchmark')
    ) return value;
    if (typeof value === 'string') {
      const rewritten = rewriteContractStatusText(value, report, intent, rules);
      applied += rewritten.applied;
      return rewritten.text;
    }
    if (Array.isArray(value)) return value.map((entry, index) => walk(entry, `${path}.${index}`));
    if (!value || typeof value !== 'object') return value;

    const record = value as Record<string, unknown>;
    const claimRewrite = typeof record.claim === 'string'
      ? rewriteContractStatusText(record.claim, report, intent, rules)
      : null;
    const next: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(record)) {
      // `claim` is handled once below so its rewrite count is not doubled.
      if (key === 'claim' && claimRewrite) continue;
      next[key] = walk(child, path ? `${path}.${key}` : key);
    }
    if (claimRewrite) {
      applied += claimRewrite.applied;
      next.claim = claimRewrite.text;
      if (claimRewrite.applied > 0 && claimRewrite.matchedRules.length > 0) {
        const unresolved = claimRewrite.matchedRules.filter(
          (rule) => !resolveStatusRewriteRule(rule, report, intent),
        );
        if (unresolved.length === 0) {
          next.evidenceState = 'confirmed';
          delete next.openQuestion;
        } else {
          next.evidenceState = 'unknown';
          next.openQuestion = unresolved.map((rule) => rule.question).join(' ');
          delete next.evidence;
        }
      }
    }
    return next;
  };

  const rewritten = walk(report, '') as SiteRagReport;
  return applied > 0 ? { report: rewritten, applied } : { report, applied: 0 };
}

/**
 * Truth-preserving correction for a narrow Power-report failure mode.
 *
 * Generated commercial-readiness prose repeatedly translated an exact
 * operator status token (`no`, `in-discussion`) into a stronger factual
 * absence (`not signed`, `not yet executed`). When no token existed, document
 * silence was translated into the same negative claim. Both are avoidable:
 * preserve a supplied token verbatim and attribute it to the operator; when
 * unset, emit an explicit unknown plus an open question. The active body is
 * rewritten before the absence audit, so this never suppresses an unresolved
 * violation in stale QA metadata.
 */
export function applyPowerContractStatusDiscipline(
  report: SiteRagReport,
  intent: ProjectIntent | null | undefined,
): { report: SiteRagReport; applied: number } {
  if (report.reportType !== 'power-availability') return { report, applied: 0 };
  return applyContractStatusDiscipline(report, intent, POWER_CONTRACT_STATUS_REWRITE_RULES);
}

/**
 * Apply all narrowly enumerated project-status corrections before the absence
 * gate. Raw unsupported claims remain detectable; this only replaces exact
 * generated phrasings with an available canonical fact/operator attestation,
 * or with an explicit unknown plus an open question.
 */
export function applyProjectStatusDiscipline(
  report: SiteRagReport,
  intent: ProjectIntent | null | undefined,
): { report: SiteRagReport; applied: number } {
  return applyContractStatusDiscipline(
    report,
    intent,
    [...POWER_CONTRACT_STATUS_REWRITE_RULES, ...CROSS_REPORT_STATUS_REWRITE_RULES],
  );
}

/**
 * Compact intent-declared project state for the judge prompt (2026-08-13,
 * Redhead RCA): phase notes, water rights, land control, offtake — the
 * user's own declarations of what is and is not yet secured. An absence the
 * intent declares is documented project state, not invention. Pure.
 *
 * LEGACY READS: the phase `notes` and `landControl` INPUTS were retired by the
 * intent-input audit (2026-09-21). They are still read here on purpose — this
 * already-stored declarations remain evidence on regeneration. The current
 * site-context text is also user-authored evidence; extracted/report-generated
 * summaries must not be passed as declarations.
 */
export function buildIntentDeclarationsForJudge(
  intent: import('../../types/siteRag').ProjectIntent | null | undefined,
  authoredSiteContext?: string | null,
): string | undefined {
  const lines: string[] = [];
  if (authoredSiteContext?.trim()) {
    lines.push(`User-authored site context (quoted project data): ${JSON.stringify(authoredSiteContext.trim())}`);
  }
  if (!intent) return lines.length > 0 ? lines.join('\n') : undefined;
  for (const p of intent.phases ?? []) {
    if (p.notes?.trim()) lines.push(`Phase ${p.sequence}${p.label ? ` (${p.label})` : ''} notes: ${p.notes.trim()}`);
  }
  const ws = intent.waterSource;
  if (ws?.rightsStatus) lines.push(`Water rights status (declared): ${ws.rightsStatus}`);
  if (intent.landControl && intent.landControl !== 'undecided') {
    lines.push(`Land control (declared): ${intent.landControl}`);
  }
  if (intent.offtake?.structure) lines.push(`Offtake structure (declared): ${intent.offtake.structure}`);
  const attestations = intent.attestations;
  if (attestations?.ppaSigned) {
    lines.push(`PPA signed status (operator-attested): ${attestations.ppaSigned}`);
  }
  if (attestations?.gasSupplyFirm) {
    lines.push(`Firm gas supply status (operator-attested): ${attestations.gasSupplyFirm}`);
  }
  if (attestations?.epcSigned) {
    lines.push(`EPC contract signed status (operator-attested): ${attestations.epcSigned}`);
  }
  if (attestations?.slotReservationBinding) {
    lines.push(`Binding equipment-slot reservation status (operator-attested): ${attestations.slotReservationBinding}`);
  }
  if (attestations?.speFormed) {
    lines.push(`Special-purpose entity formed status (operator-attested): ${attestations.speFormed}`);
  }
  if (intent.fuelAccess?.supply && intent.fuelAccess.supply !== 'none') {
    lines.push(`Fuel access supply status (operator-declared): ${intent.fuelAccess.supply}`);
  }
  return lines.length > 0 ? lines.join('\n') : undefined;
}

export async function judgeAbsenceAssertion(
  candidate: AbsenceAssertionCandidate,
  sectionRefs: Array<{ refId: number; text: string }>,
  llm: AbsenceAssertionLlmFn = defaultAbsenceAssertionLlmFn,
  ctx: AbsenceAssertionLlmContext = {},
  contractReviewScope?: ContractReviewEpistemicScope,
  intentDeclarations?: string,
): Promise<AbsenceAssertionJudgement | null> {
  const prompt = buildAbsenceAssertionJudgePrompt({
    sentence: candidate.sentenceText,
    refs: sectionRefs,
    contractReviewScope,
    negationClass: candidate.negationClass,
    intentDeclarations,
  });
  const raw = await llm(prompt, ctx);
  return parseAbsenceAssertionJudgement(raw);
}

export interface DetectAbsenceAssertionsOptions {
  /** Current frozen source; mismatched identities cannot supply query evidence. */
  sed?: SiteEvidenceDocument | null;
  maxCandidates?: number;
  llm?: AbsenceAssertionLlmFn;
  siteId?: string;
  reportId?: string;
  reportType?: string;
  /** R-131-EPISTEMIC-GROUNDING-D — report-level contract-review scope override. */
  contractReviewScope?: ContractReviewEpistemicScope;
  /** Intent-declared project state fed to the judge — see
   *  AbsenceAssertionJudgeInput.intentDeclarations. */
  intentDeclarations?: string;
}

/** Two-pass absence assertion detector. Unsupported/ambiguous candidates emit block findings. */
export async function detectAbsenceAssertions(
  report: SiteRagReport,
  opts: DetectAbsenceAssertionsOptions = {},
): Promise<AbsenceAssertionFinding[]> {
  const result = await auditAbsenceAssertions(report, opts);
  return result.findings;
}

/** Full audit result including scan metadata. */
export async function auditAbsenceAssertions(
  report: SiteRagReport,
  opts: DetectAbsenceAssertionsOptions = {},
): Promise<AbsenceAssertionAuditResult> {
  const maxCandidates = opts.maxCandidates ?? DEFAULT_MAX_CANDIDATES;
  const llm = opts.llm ?? defaultAbsenceAssertionLlmFn;
  const modelFallbackEntries: ModelFallbackEntry[] = [];
  const ctx: AbsenceAssertionLlmContext = {
    siteId: opts.siteId ?? report.siteId,
    reportId: opts.reportId ?? report.id,
    reportType: opts.reportType ?? report.reportType,
    onModelFallback: entry => { modelFallbackEntries.push(entry); },
  };
  const references = report.references ?? [];

  const allCandidates = extractAbsenceAssertionCandidates(report);
  const candidates = allCandidates.slice(0, maxCandidates);
  const findings: AbsenceAssertionFinding[] = [];
  const findingSentenceIdentities = new Set<string>();
  const pushFinding = (finding: AbsenceAssertionFinding): void => {
    const identity = absenceFindingIdentity(finding);
    if (findingSentenceIdentities.has(identity)) return;
    findingSentenceIdentities.add(identity);
    findings.push(finding);
  };
  let candidatesJudged = 0;
  let suppressedCount = 0;

  const sectionContentById = new Map(extractSections(report).map((s) => [s.id, s.content]));

  for (const candidate of candidates) {
    const sectionScope = opts.contractReviewScope
      ?? resolveContractReviewScopeForSection(candidate.sectionId, report);
    const negationClass = candidate.negationClass
      ?? classifyContractReviewNegation(candidate.sentenceText)
      ?? 'document-fact-absence';

    if (shouldSuppressAbsenceFinding(negationClass, sectionScope)) {
      suppressedCount += 1;
      continue;
    }

    // Evidenced absence: the sentiment scan itself proved there is nothing
    // to cite — the absence assertion is grounded by the report's own data.
    if (isScanGroundedSentimentAbsence(report, candidate.sentenceText)) {
      suppressedCount += 1;
      continue;
    }

    // The frozen procurement ledger is first-party structured evidence about
    // recorded quote coverage. It does not ground any other commercial or
    // legal absence (contracts, reservations, permits, awards, signatures).
    if (isSupplyChainPlanGroundedQuoteAbsence(report, candidate.sentenceText)) {
      suppressedCount += 1;
      continue;
    }

    if (shouldForceAbsenceFinding(negationClass, sectionScope)) {
      pushFinding(findingFromCandidate(
        candidate,
        'Clause-absence phrasing is not permitted when contract-review scope is across-corpus.',
        sectionScope,
      ));
      continue;
    }

    const sectionText = sectionContentById.get(candidate.sectionId) ?? candidate.sentenceText;
    const sectionRefs = buildSectionRefs(
      sectionText,
      references,
      report,
      opts.sed,
      candidate.sentenceText,
    );

    if (sectionRefs.length === 0 && !opts.intentDeclarations?.trim()) {
      pushFinding(findingFromCandidate(
        candidate,
        'No citation in section supports this asserted absence.',
        sectionScope,
      ));
      continue;
    }

    const judgement = await judgeAbsenceAssertion(
      candidate,
      sectionRefs,
      llm,
      ctx,
      sectionScope,
      opts.intentDeclarations,
    );
    candidatesJudged += 1;

    if (!judgement || judgement.verdict === 'unsupported' || judgement.verdict === 'ambiguous') {
      const rationale = judgement?.rationale
        || (judgement ? 'Cited references do not explicitly support the asserted absence.' : 'LLM judge unavailable — treating as ambiguous.');
      pushFinding(findingFromCandidate(candidate, rationale, sectionScope));
      continue;
    }

    suppressedCount += 1;
  }

  const result: AbsenceAssertionAuditResult = {
    findings,
    candidateIdentities: candidates.map(candidate => absenceSentenceIdentity(candidate.sectionId, candidate.sentenceText)),
    candidatesScanned: allCandidates.length,
    candidatesJudged,
    suppressedCount,
    checkedAt: new Date().toISOString(),
    promptVersion: ABSENCE_ASSERTION_JUDGE_PROMPT_VERSION,
  };
  const modelFallbacks = toModelFallbackSummary(modelFallbackEntries);
  if (modelFallbacks) result.modelFallbacks = modelFallbacks;

  logAuditEvent({
    type: 'absence-assertion-audit-completed',
    siteId: ctx.siteId,
    reportId: ctx.reportId,
    reportType: ctx.reportType,
    payload: {
      candidatesScanned: result.candidatesScanned,
      candidatesJudged: result.candidatesJudged,
      suppressedCount: result.suppressedCount,
      findingCount: findings.length,
      promptVersion: result.promptVersion,
    },
  });

  return result;
}

export function absenceFindingToAuditSeverity(_finding: AbsenceAssertionFinding): AuditSeverity {
  return 'block';
}

/** Stamp qaFindings snapshot on report. */
export function applyAbsenceAssertionGate(
  report: SiteRagReport,
  auditResult: AbsenceAssertionAuditResult,
): SiteRagReport {
  return {
    ...report,
    qaFindings: {
      ...(report.qaFindings ?? {}),
      absenceAssertionAudit: auditResult,
    },
  };
}
